import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User,
  Lock,
  ShieldCheck,
  Sparkles,
  Loader2,
  Info,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { LOGIN } from "@/constants/testIds/auth";
import { api, apiErrorMessage } from "@/lib/api";
import { setSession, isViewerRoute } from "@/lib/auth";

const CONVOCATION_BANNER = `${process.env.PUBLIC_URL}/convocation-login-banner.png`;
const CONVOCATION_BANNER_2X = `${process.env.PUBLIC_URL}/convocation-login-banner@2x.png`;

function safeNextPath(raw) {
  if (!raw || typeof raw !== "string") return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  if (raw.startsWith("/login")) return null;
  return raw;
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      toast.error("Enter username and password");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", {
        username: username.trim(),
        password,
      });
      setSession(data);
      toast.success(`Welcome${data.name ? `, ${data.name}` : ""}!`);
      const next = safeNextPath(searchParams.get("next"));
      const destination = next && (data.role !== "viewer" || isViewerRoute(next)) ? next : "/dashboard";
      navigate(destination, { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Invalid credentials");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      <div className="relative hidden lg:flex items-center justify-center overflow-hidden bg-[#B60F2A] p-8 xl:p-12">
        <motion.img
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          src={CONVOCATION_BANNER}
          srcSet={`${CONVOCATION_BANNER} 1024w, ${CONVOCATION_BANNER_2X} 2048w`}
          sizes="(min-width: 1024px) 50vw, 100vw"
          alt="Mahindra University campus"
          className="max-h-[min(92vh,720px)] w-full max-w-[min(96%,920px)] h-auto object-contain rounded-2xl border-4 border-white/90 shadow-[0_20px_50px_rgba(0,0,0,0.25)] [image-rendering:auto]"
          decoding="async"
          data-testid="login-convocation-banner"
        />
      </div>

      <div className="flex flex-col min-h-screen">
        <div className="lg:hidden p-4 border-b border-[#E5E7EB] bg-[#B60F2A] flex justify-center">
          <img
            src={CONVOCATION_BANNER}
            srcSet={`${CONVOCATION_BANNER} 1024w, ${CONVOCATION_BANNER_2X} 2048w`}
            sizes="100vw"
            alt="Mahindra University campus"
            className="max-h-44 w-full max-w-md object-contain px-2 rounded-xl border-2 border-white/90 shadow-md"
            decoding="async"
          />
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-md"
            data-testid="login-card"
          >
            <div className="inline-flex items-center gap-2 text-xs font-semibold bg-[#FBE7EA] text-[#B60F2A] px-3 py-1 rounded-full">
              <Sparkles className="h-3.5 w-3.5" />
              Convocation Portal
            </div>

            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight text-[#111]">
              Sign in to continue
            </h2>
            <p className="text-[#6B7280] mt-2">
              Staff access for registrar, check-in, and backstage teams.
            </p>

            <div className="mt-6 rounded-xl border border-[#FBE7EA] bg-[#FBE7EA]/40 p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-[#B60F2A] mt-0.5 shrink-0" />
                <p className="text-sm text-[#111] leading-relaxed">
                  Use your convocation staff credentials. The announcer display
                  at <span className="font-medium">/announcer</span> stays open
                  for venue screens without sign-in.
                </p>
              </div>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#111] mb-1.5" htmlFor="username">
                  Username
                </label>
                <div className="relative">
                  <User className="h-4 w-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <Input
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username"
                    autoComplete="username"
                    className="h-12 pl-10 rounded-xl border-[#E5E7EB] bg-white text-[#111] placeholder:text-[#9CA3AF] focus-visible:ring-[#B60F2A]/30"
                    data-testid={LOGIN.emailInput}
                    disabled={submitting}
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#111] mb-1.5" htmlFor="password">
                  Password
                </label>
                <div className="relative">
                  <Lock className="h-4 w-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    autoComplete="current-password"
                    className="h-12 pl-10 pr-10 rounded-xl border-[#E5E7EB] bg-white text-[#111] placeholder:text-[#9CA3AF] focus-visible:ring-[#B60F2A]/30"
                    data-testid={LOGIN.passwordInput}
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111]"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting || !username.trim() || !password}
                className="w-full h-12 mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#B60F2A] text-white font-semibold hover:bg-[#9a0d24] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-[0_10px_24px_-12px_rgba(182,15,42,0.7)]"
                data-testid={LOGIN.submitButton}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                {submitting ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
